import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import Table from "../../core/pagination/datatable";
import { fetchSuppliers } from "../../core/redux/slices/supplierSlice";
import { getSupplierStatement } from "../../core/api/reportService";

const today = () => new Date().toISOString().split("T")[0];
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; };
const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const TYPE_BADGE = { Purchase: "primary", Payment: "success", Advance: "info", Return: "warning" };

const SupplierStatement = () => {
  const dispatch = useDispatch();
  const suppliers = useSelector((s) => s.suppliers.list);

  const [supplierId, setSupplierId] = useState("");
  const [filters, setFilters] = useState({ from: firstOfMonth(), to: today() });
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleReset = () => {
    setSupplierId("");
    setFilters({ from: firstOfMonth(), to: today() });
    setData([]);
    setSummary(null);
  };

  const supplierOptions = suppliers.map((s) => ({ value: s.id, label: s.supplierName }));

  useEffect(() => { dispatch(fetchSuppliers()); }, [dispatch]);

  const runReport = async () => {
    if (!supplierId) { Swal.fire("Required", "Please select a supplier.", "warning"); return; }
    setLoading(true);
    try {
      const res = await getSupplierStatement({ supplierId, ...filters });
      const payload = res.data || {};
      setData(payload.records || payload || []);
      setSummary(payload.summary || null);
    } catch {
      Swal.fire("Error", "Failed to load statement.", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Date", dataIndex: "date", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    {
      title: "Type", dataIndex: "docType", width: 100,
      render: (v) => <span className={`badge bg-${TYPE_BADGE[v] || "secondary"}`}>{v}</span>,
    },
    { title: "Reference", dataIndex: "docNo", width: 150 },
    { title: "Debit", dataIndex: "debit", align: "right", width: 120, render: (v) => v ? <span className="text-danger">{fmt(v)}</span> : "—" },
    { title: "Credit", dataIndex: "credit", align: "right", width: 120, render: (v) => v ? <span className="text-success">{fmt(v)}</span> : "—" },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title"><h4>Supplier Statement</h4><h6>Supplier-wise transaction statement</h6></div>
        </div>
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col" style={{ minWidth: 220, maxWidth: 300 }}>
                <label className="form-label small mb-1">Supplier <span className="text-danger">*</span></label>
                <Select
                  classNamePrefix="react-select"
                  options={supplierOptions}
                  value={supplierOptions.find((o) => o.value === supplierId) || null}
                  onChange={(opt) => setSupplierId(opt?.value || "")}
                  placeholder="Select supplier"
                  isClearable
                />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">From</label>
                <input type="date" className="form-control form-control-sm" value={filters.from}
                  onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} />
              </div>
              <div className="col-auto">
                <label className="form-label small mb-1">To</label>
                <input type="date" className="form-control form-control-sm" value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} />
              </div>
              <div className="col-auto">
                <button className="btn btn-primary" onClick={runReport} disabled={loading}>
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="col-auto">
                <button className="btn btn-dark" onClick={handleReset} disabled={loading}>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row mb-3">
            {[
              { label: "Total Purchases", value: fmt(summary.totalPurchases), color: "primary" },
              { label: "Total Paid", value: fmt(summary.totalPaid), color: "success" },
              { label: "Outstanding", value: fmt(summary.outstanding), color: "danger" },
            ].map((s) => (
              <div key={s.label} className="col-6 col-md-3 mb-2">
                <div className={`card border-${s.color} text-center py-2`}>
                  <div className={`text-${s.color} fs-6 fw-bold`}>{s.value}</div>
                  <div className="small text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplierStatement;
